export function sanitizeArtifactDraftContent(
  content: string | undefined | null,
): string {
  return content
    ? content
        .replace(/\[Using tool: [^\]]+\]/g, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
    : '';
}

export interface ToolUseSummary {
  tools: string[];
  totalEvents: number;
}

const TOOL_USE_MARKER_REGEX = /^\[\s*Using tool:\s*([^\]]+)\]\s*$/i;

function buildToolSummaryLine(tools: string[], totalEvents: number): string {
  const suffix = totalEvents === 1 ? 'evento' : 'eventos';
  return `Ferramentas: ${tools.join(', ')} (${totalEvents} ${suffix})`;
}

export function summarizeToolUseMarkersForDisplay(
  content: string | undefined | null,
): { content: string; summary: ToolUseSummary | null } {
  if (!content) {
    return { content: '', summary: null };
  }

  const lines = content.split('\n');
  const output: string[] = [];
  const allTools: string[] = [];
  const allToolsSet = new Set<string>();
  let totalEvents = 0;
  let idx = 0;

  while (idx < lines.length) {
    const currentLine = lines[idx];
    const currentMatch = currentLine.trim().match(TOOL_USE_MARKER_REGEX);

    if (!currentMatch) {
      output.push(currentLine);
      idx += 1;
      continue;
    }

    const blockTools: string[] = [];
    const blockToolsSet = new Set<string>();
    let blockEvents = 0;

    while (idx < lines.length) {
      const line = lines[idx];
      const trimmedLine = line.trim();

      if (trimmedLine.length === 0) {
        let lookahead = idx + 1;
        while (
          lookahead < lines.length &&
          lines[lookahead].trim().length === 0
        ) {
          lookahead += 1;
        }

        if (
          lookahead < lines.length &&
          TOOL_USE_MARKER_REGEX.test(lines[lookahead].trim())
        ) {
          idx = lookahead;
          continue;
        }
        break;
      }

      const markerMatch = trimmedLine.match(TOOL_USE_MARKER_REGEX);
      if (!markerMatch) {
        break;
      }

      const toolName = markerMatch[1].trim();
      blockEvents += 1;
      totalEvents += 1;

      if (toolName && !blockToolsSet.has(toolName)) {
        blockToolsSet.add(toolName);
        blockTools.push(toolName);
      }
      if (toolName && !allToolsSet.has(toolName)) {
        allToolsSet.add(toolName);
        allTools.push(toolName);
      }

      idx += 1;
    }

    if (blockEvents > 0) {
      output.push(buildToolSummaryLine(blockTools, blockEvents));
      continue;
    }

    output.push(currentLine);
    idx += 1;
  }

  return {
    content: output.join('\n').replace(/\n{3,}/g, '\n\n'),
    summary:
      totalEvents > 0
        ? {
            tools: allTools,
            totalEvents,
          }
        : null,
  };
}

export function normalizeRecoverySnapshotContent(
  content: string | undefined | null,
): string {
  return content ? content.replace(/\n{3,}/g, '\n\n').trim() : '';
}

export function findRecoveryContentOverlap(
  base: string,
  continuation: string,
): number {
  const maxOverlap = Math.min(base.length, continuation.length);

  for (let overlap = maxOverlap; overlap > 0; overlap -= 1) {
    if (base.slice(-overlap) === continuation.slice(0, overlap)) {
      return overlap;
    }
  }

  return 0;
}

function normalizeRecoveryComparableSegment(
  content: string | undefined | null,
): string {
  return normalizeRecoverySnapshotContent(content)
    .split('\n')
    .map(line =>
      line.replace(/^\s{0,3}(?:#{1,6}\s*|[-*+]\s*|\d+[.)]\s*)?/, '').trim(),
    )
    .filter(Boolean)
    .join(' ')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function mergeRecoverySupersededTail(
  snapshot: string,
  live: string,
): string | null {
  const snapshotParagraphs = snapshot
    .split(/\n{2,}/)
    .map(paragraph => normalizeRecoverySnapshotContent(paragraph))
    .filter(Boolean);
  const liveParagraphs = live
    .split(/\n{2,}/)
    .map(paragraph => normalizeRecoverySnapshotContent(paragraph))
    .filter(Boolean);

  if (snapshotParagraphs.length === 0 || liveParagraphs.length === 0) {
    return null;
  }

  const snapshotTail = snapshotParagraphs[snapshotParagraphs.length - 1];
  const liveHead = liveParagraphs[0];
  const comparableSnapshotTail =
    normalizeRecoveryComparableSegment(snapshotTail);
  const comparableLiveHead = normalizeRecoveryComparableSegment(liveHead);

  if (!comparableSnapshotTail || !comparableLiveHead) {
    return null;
  }

  const sameParagraphFamily =
    comparableSnapshotTail.includes(comparableLiveHead) ||
    comparableLiveHead.includes(comparableSnapshotTail);

  if (!sameParagraphFamily) {
    return null;
  }

  return normalizeRecoverySnapshotContent(
    [
      ...snapshotParagraphs.slice(0, -1),
      liveHead,
      ...liveParagraphs.slice(1),
    ].join('\n\n'),
  );
}

export function mergeRecoveryStreamingContent(
  snapshotContent: string | undefined | null,
  liveStreamingContent: string | undefined | null,
): string {
  const snapshot = normalizeRecoverySnapshotContent(snapshotContent);
  const live = normalizeRecoverySnapshotContent(liveStreamingContent);

  if (!snapshot) {
    return live;
  }

  if (!live) {
    return snapshot;
  }

  if (snapshot === live || snapshot.includes(live)) {
    return snapshot;
  }

  if (live.includes(snapshot)) {
    return live;
  }

  const snapshotWithoutTools = sanitizeArtifactDraftContent(snapshot);
  const liveWithoutTools = sanitizeArtifactDraftContent(live);

  if (
    snapshotWithoutTools &&
    (snapshotWithoutTools === live || snapshotWithoutTools.includes(live))
  ) {
    return snapshot;
  }

  if (
    liveWithoutTools &&
    (liveWithoutTools === snapshot || liveWithoutTools.includes(snapshot))
  ) {
    return live;
  }

  const supersededTailMerge = mergeRecoverySupersededTail(snapshot, live);
  if (supersededTailMerge) {
    return supersededTailMerge;
  }

  const overlap = findRecoveryContentOverlap(snapshot, live);
  if (overlap > 0) {
    return normalizeRecoverySnapshotContent(
      `${snapshot}${live.slice(overlap)}`,
    );
  }

  return normalizeRecoverySnapshotContent(`${snapshot}\n\n${live}`);
}
